import { apiService } from 'core/services/apiService';
import { paginationService } from 'core/services/paginationService';

export interface JobCode {
  "@id": string;
  "@type": string;
  "id": number;
  "jobNumber": number;
  "label": string;
  "backgroundColor": string;
  "textColor": string;
  "category": string;
}

export const getJobCodes = async (): Promise<JobCode[]> => {
  const data = await paginationService.getWithHandler('job_codes');
  return data?.member || data?.['hydra:member'] || [];
};

export const createJobCode = async (jobCode: Partial<JobCode>): Promise<JobCode> => {
  const { data } = apiService.responseHandler(
    await apiService.post('job_codes', jobCode)
  );
  return data;
};

export const updateJobCode = async (id: number, jobCode: Partial<JobCode>): Promise<JobCode> => {
  const { data } = apiService.responseHandler(
    await apiService.put(`job_codes/${id}`, jobCode)
  );
  return data;
};

export const deleteJobCode = async (id: number): Promise<void> => {
  await apiService.delete(`job_codes/${id}`);
};

// Time Off Reasons API
export interface TimeOffReason {
  "@id": string;
  "@type": string;
  "id": number;
  "name": string;
  "description": string | null;
  "note": string | null;
  "requiresApproval": boolean;
  "active": boolean;
  "deleted": boolean;
  "created_at": string | null;
  "updated_at": string | null;
}

export interface TimeOffReasonRequest {
  name: string;
  description: string;
}

export const getTimeOffReasons = async (): Promise<TimeOffReason[]> => {
  const data = await paginationService.getWithHandler('scheduler_time_off_reasons');
  return data?.member || data?.['hydra:member'] || [];
};

export const createTimeOffReason = async (timeOffReason: TimeOffReasonRequest): Promise<TimeOffReason> => {
  const { data } = apiService.responseHandler(
    await apiService.post('scheduler_time_off_reasons', timeOffReason)
  );
  return data;
};

export const updateTimeOffReason = async (id: number, timeOffReason: TimeOffReasonRequest): Promise<TimeOffReason> => {
  const { data } = apiService.responseHandler(
    await apiService.put(`scheduler_time_off_reasons/${id}`, timeOffReason)
  );
  return data;
};

export const deleteTimeOffReason = async (id: number): Promise<void> => {
  await apiService.delete(`scheduler_time_off_reasons/${id}`);
};

// General Settings API
// Import GeneralSettings types from centralized location
import type { GeneralSettings, GeneralSettingsRequest } from '../../types/scheduler';

export type { GeneralSettings, GeneralSettingsRequest };

export const getGeneralSettings = async (): Promise<GeneralSettings | null> => {
  try {
    const data = await paginationService.getWithHandler('scheduler_settings');
    const settings = data?.member || data?.['hydra:member'] || [];
    return settings.length > 0 ? settings[0] : null;
  } catch (error) {
    console.error('Error fetching general settings:', error);
    return null;
  }
};

export const createGeneralSettings = async (settings: GeneralSettingsRequest): Promise<GeneralSettings> => {
  const { data } = apiService.responseHandler(
    await apiService.post('scheduler_settings', settings)
  );
  return data;
};

export const updateGeneralSettings = async (id: number, settings: GeneralSettingsRequest): Promise<GeneralSettings> => {
  const { data } = apiService.responseHandler(
    await apiService.put(`scheduler_settings/${id}`, settings)
  );
  return data;
};

// Scheduler Metrics
export interface SchedulerMetric {
  "@id": string;
  "@type": "SchedulerMetric";
  id: number;
  workplace: string; // IRI like "/api/workplaces/{id}"
  day: string; // Date in YYYY-MM-DD format
  splh: number; // Sales Per Labor Hour
  cplh: number; // Customers Per Labor Hour
  hoursScheduled: number; // Total hours scheduled for the day
  otHoursScheduled: number; // Overtime hours scheduled
  sales: number; // Total sales for the day
  customers: number; // Total customers for the day
  active: boolean;
  deleted: boolean;
  created_at: string;
  updated_at: string;
}

export interface SchedulerMetricRequest {
  workplace: string; // IRI like "/api/workplaces/{id}"
  day: string; // Date in YYYY-MM-DD format
  sales: number; // Total sales for the day
  customers: number; // Total customers for the day
  hoursScheduled: number; // Total hours scheduled for the day
  otHoursScheduled: number; // Overtime hours scheduled
  splh?: number; // Sales Per Labor Hour (optional, calculated from sales/hoursScheduled)
  cplh?: number; // Customers Per Labor Hour (optional, calculated from customers/hoursScheduled)
}

export const getSchedulerMetrics = async (workplaceId: number, startDate?: string, endDate?: string): Promise<SchedulerMetric[]> => {
  try {
    let endpoint = `scheduler_metrics?workplace=${workplaceId}`;
    if (startDate) endpoint += `&from=${startDate}`;
    if (endDate) endpoint += `&to=${endDate}`;
    
    const data = apiService.responseHandler(await apiService.get(endpoint));
    return data.member || [];
  } catch (error) {
    console.error('Error fetching scheduler metrics:', error);
    return [];
  }
};

export const createSchedulerMetric = async (metric: SchedulerMetricRequest): Promise<SchedulerMetric> => {
  const { data } = apiService.responseHandler(
    await apiService.post('scheduler_metrics', metric)
  );
  return data;
};

export const updateSchedulerMetric = async (id: number, metric: SchedulerMetricRequest): Promise<SchedulerMetric> => {
  const { data } = apiService.responseHandler(
    await apiService.put(`scheduler_metrics/${id}`, metric)
  );
  return data;
};